import styled from '@emotion/styled'
import { ListItemButton } from '@mui/material'
import { SyntheticEvent, memo, useCallback } from 'react'
import { openMenuDialog } from '../../../actions/menu'
import { SearchNotesResults } from '../../../actions/notes'
import i18n from '../../../utilities/i18n'
import { safeObj } from '../../../utilities/safe'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { maxWidth, minWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import NoteName from './NoteName'

// TODO: virtualize

const NotesListRoot = styled('div')({
    display: 'flex',
    width: '100%',
    height: '100%',
    overflowY: 'scroll',
    padding: getThemeSpacing(0.2),
    // ...NO_SCROLLBAR_STYLES,
})
const StyledListItem = styled(ListItemButton)({
    padding: 0,
    cursor: 'pointer',
})
const ListItemContent = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
    padding: getThemeSpacing(0.5),
})
const NotesWrapper = styled('div')({
    display: 'flex',
    width: '100%',
    height: '100%',
    flexFlow: 'row wrap',
    alignItems: 'baseline',
    padding: `${getThemeSpacing(0.25)} ${getThemeSpacing(0.5)}`,
})
const NotesBody = styled('div')({
    width: '100%',
    height: '100%',
    fontFamily: 'inherit',
    borderColor: palette.drawerDivider,
})
const SearchResultNote = styled('div')({
    fontSize: 12,
})
const ClearedSearchResultsRoot = styled('div')({
    display: 'flex',
    width: '100%',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: getThemeSpacing(0.5),
    padding: getThemeSpacing(2),
    background: palette.drawerDivider,
    [maxWidth(APP_BREAKPOINTS.sm)]: {
        padding: getThemeSpacing(0.5),
    },
    [minWidth(APP_BREAKPOINTS.sm)]: {
        padding: getThemeSpacing(1),
    },
})

interface ISearchResultsRow {
    notes: Array<string>
    onSelect?(userId: number): void
    userId: number
}

const SearchResultsRow = memo((props: ISearchResultsRow) => {
    const { userId, notes, onSelect = () => {} } = props
    const slug = useUserSelector(userId, 'slug')
    const name = useUserSelector(userId, 'name')

    const onClick = useCallback(() => {
        onSelect(userId)
    }, [userId])

    const onOpenMenu = useCallback(
        (e: SyntheticEvent) => {
            e.stopPropagation()
            openMenuDialog(slug, { hideNextPrev: true })
        },
        [slug],
    )

    return (
        <StyledListItem key={userId} onClick={onClick} disableGutters dense>
            <ListItemContent>
                <NoteName
                    userId={userId}
                    username={name}
                    onClick={onOpenMenu}
                />
                <SearchResultNote
                    dangerouslySetInnerHTML={{ __html: notes[0] }}
                />
            </ListItemContent>
        </StyledListItem>
    )
})

interface ISearchResults {
    data: SearchNotesResults
    onSelect(userId: number): void
}

const SearchResults = memo((props: ISearchResults) => {
    const { data, onSelect } = props
    const { total_found, results } = safeObj(data)

    if (total_found === 0) {
        return (
            <ClearedSearchResultsRoot data-testid="MyNotes__SearchResults__EmptyPanel">
                {i18n.t('chat.noResultsFound')}
            </ClearedSearchResultsRoot>
        )
    }

    return (
        <NotesListRoot
            className="no-scrollbars"
            data-testid="MyNotes__SearchResults__List"
        >
            <NotesWrapper>
                <NotesBody>
                    {Object.values(results).map((result) => {
                        const { subject_id: userId, results: notes } = result
                        return (
                            <SearchResultsRow
                                key={userId}
                                userId={userId}
                                notes={notes}
                                onSelect={onSelect}
                            />
                        )
                    })}
                </NotesBody>
            </NotesWrapper>
        </NotesListRoot>
    )
})

export default SearchResults
